import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  IconButton,
  Chip,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Snackbar,
  Avatar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  CalendarToday,
  AccessTime,
  CheckCircle,
  Cancel,
  Warning,
  Schedule,
  LocationOn,
  TrendingUp,
  Download,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import userService from '../../services/userService';
import attendanceService, { AttendanceRecord, AttendanceStats } from '../../services/attendanceService';
import { User } from '../../types';
import ProfileAvatar from '../../components/common/ProfileAvatar';
import ImageDialog from '../../components/common/ImageDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-attendance-tabpanel-${index}`}
      aria-labelledby={`user-attendance-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const UserAttendancePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [attendanceData, setAttendanceData] = useState<AttendanceRecord[]>([]);
  const [attendanceStats, setAttendanceStats] = useState<AttendanceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [selectedImageAlt, setSelectedImageAlt] = useState<string>('');

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'success' | 'error' | 'warning' | 'info',
  });

  // Helper functions for snackbar
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };


  useEffect(() => {
    const loadData = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        const userData = await userService.getUserById(userId);
        setUser(userData);
      } catch (err: any) {
        showSnackbar('Failed to fetch user details', 'error');
      }
      setLoading(false);
    };
    loadData();
  }, [userId]);

  useEffect(() => {
    const loadAttendanceData = async () => {
      if (!userId || !user) return;
      
      try {
        const startDate = new Date(selectedYear, selectedMonth, 1).toISOString().split('T')[0];
        const endDate = new Date(selectedYear, selectedMonth + 1, 0).toISOString().split('T')[0];
        
        const response = await attendanceService.getUserAttendance(userId, {
          startDate,
          endDate,
        });
        
        setAttendanceData(response.records);
        setAttendanceStats(response.stats);
      } catch (err: any) {
        console.warn('Failed to load user attendance:', err);
        showSnackbar(err?.response?.data?.message || 'Failed to load attendance', 'error');
        setAttendanceData([]);
        setAttendanceStats({
          totalDays: 0,
          presentDays: 0,
          absentDays: 0,
          lateDays: 0,
          totalHours: 0,
          averageHours: 0,
          attendancePercentage: 0,
        });
      }
    };
    
    if (user) {
      loadAttendanceData();
    }
  }, [userId, user, selectedMonth, selectedYear]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleMonthChange = (month: number) => {
    setSelectedMonth(month);
  };

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
  };

  const getStatusLabel = (status: string, isLate?: boolean, lateMinutes?: number) => {
    if (isLate) { // Show "Late" if is_late is true, regardless of status
      return lateMinutes ? `${lateMinutes}m Late` : 'Late';
    }
    switch (status) {
      case 'in': return 'Present';
      case 'out': return 'Out';
      case 'absent': return 'Absent';
      case 'late': return 'Late';
      case 'no_punch': return 'No Punch';
      default: return (status || '').charAt(0).toUpperCase() + (status || '').slice(1);
    }
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return '-';
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  const getStatusColor = (status: string, isLate?: boolean) => {
    if (isLate) { // Show warning (orange) if late, regardless of status
      return 'warning';
    }
    switch (status) {
      case 'in':
      case 'present': return 'success';
      case 'absent': return 'error';
      case 'late': return 'warning';
      case 'out': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string, isLate?: boolean) => {
    if (isLate) { // Show warning icon if late, regardless of status
      return <Warning />;
    }
    switch (status) {
      case 'in':
      case 'present': return <CheckCircle />;
      case 'absent': return <Cancel />;
      case 'late': return <Warning />;
      case 'out': return <Schedule />;
      default: return <CalendarToday />;
    }
  };

  const getMonthName = (month: number) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month];
  };

  // Date navigation functions
  const handlePreviousMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const handleCurrentMonth = () => {
    const now = new Date();
    setSelectedMonth(now.getMonth());
    setSelectedYear(now.getFullYear());
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          User not found
        </Alert>
        <Button onClick={() => navigate('/attendance')} variant="contained">
          Back to Attendance
        </Button>
      </Box>
    );
  }

  const stats = attendanceStats || {
    presentDays: 0,
    absentDays: 0,
    lateDays: 0,
    totalHours: 0,
    averageHours: 0,
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={() => navigate('/attendance')} sx={{ color: '#081122' }}>
            <ArrowBack />
          </IconButton>
          <ProfileAvatar
            imageUrl={user.profile_image_url}
            firstName={user.first_name}
            lastName={user.last_name}
            sx={{ width: 48, height: 48, bgcolor: '#081122' }}
          />
          <Box>
            <Typography variant="h4" component="h1" sx={{ color: '#081122', fontWeight: 600 }}>
              {user.first_name} {user.last_name}
            </Typography>
            <Typography variant="body1" sx={{ color: '#666666' }}>
              {user.employee_id} | {user.store?.name || 'N/A'} | {user.cluster?.name || 'N/A'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <CheckCircle sx={{ fontSize: 32, color: '#4caf50' }} />
              </Box>
              <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 600 }}>
                {stats.presentDays}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                Present Days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Cancel sx={{ fontSize: 32, color: '#f44336' }} />
              </Box>
              <Typography variant="h4" sx={{ color: '#f44336', fontWeight: 600 }}>
                {stats.absentDays}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                Absent Days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Warning sx={{ fontSize: 32, color: '#ff9800' }} />
              </Box>
              <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 600 }}>
                {stats.lateDays}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                Late Days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <AccessTime sx={{ fontSize: 32, color: '#2196f3' }} />
              </Box>
              <Typography variant="h4" sx={{ color: '#2196f3', fontWeight: 600 }}>
                {stats.totalHours}h
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                Total Hours
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <TrendingUp sx={{ fontSize: 32, color: '#9c27b0' }} />
              </Box>
              <Typography variant="h4" sx={{ color: '#9c27b0', fontWeight: 600 }}>
                {stats.averageHours}h
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                Avg Hours/Day
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
              Attendance Records - {getMonthName(selectedMonth)} {selectedYear}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {/* Date Navigation */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton onClick={handlePreviousMonth} size="small">
                  <ChevronLeft />
                </IconButton>
                <Button onClick={handleCurrentMonth} size="small" variant="outlined">
                  Current Month
                </Button>
                <IconButton onClick={handleNextMonth} size="small">
                  <ChevronRight />
                </IconButton>
              </Box>
              
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Month"
                  onChange={(e) => handleMonthChange(Number(e.target.value))}
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {getMonthName(i)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Year</InputLabel>
                <Select
                  value={selectedYear}
                  label="Year"
                  onChange={(e) => handleYearChange(Number(e.target.value))}
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<Download />}
                sx={{
                  borderColor: '#6c757d',
                  color: '#6c757d',
                  '&:hover': {
                    borderColor: '#081122',
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                Export
              </Button>
            </Box>
          </Box>
        </Box>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            borderBottom: '1px solid #e0e0e0',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
            },
            '& .Mui-selected': {
              color: '#081122',
            },
          }}
        >
          <Tab label="Daily Records" />
          <Tab label="Analytics" />
        </Tabs>

        {/* Daily Records Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
              Attendance Records
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Month"
                  onChange={(e) => handleMonthChange(Number(e.target.value))}
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {getMonthName(i)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Year</InputLabel>
                <Select
                  value={selectedYear}
                  label="Year"
                  onChange={(e) => handleYearChange(Number(e.target.value))}
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Day</TableCell>
                    <TableCell>Clock In</TableCell>
                    <TableCell>Clock Out</TableCell>
                    <TableCell>Total Hours</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Punch In Photo</TableCell>
                    <TableCell>Punch Out Photo</TableCell>
                    <TableCell>Location</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendanceData.map((attendance) => (
                    <TableRow key={attendance.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {new Date(attendance.date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#666666' }}>
                          {new Date(attendance.date).toLocaleDateString('en-US', { weekday: 'short' })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatTime(attendance.clock_in_time || '')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatTime(attendance.clock_out_time || '')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {attendance.total_hours && typeof attendance.total_hours === 'number' ? `${attendance.total_hours.toFixed(1)}h` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(attendance.status, attendance.is_late)}
                          label={getStatusLabel(attendance.status, attendance.is_late, attendance.late_minutes)}
                          color={getStatusColor(attendance.status, attendance.is_late) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {attendance.clock_in_photo_url ? (
                          <img
                            src={attendance.clock_in_photo_url}
                            alt="Punch In"
                            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                            onClick={() => {
                              setSelectedImageUrl(attendance.clock_in_photo_url || null);
                              setSelectedImageAlt('Punch In Photo');
                              setImageDialogOpen(true);
                            }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: '#999' }}>No photo</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {attendance.clock_out_photo_url ? (
                          <img
                            src={attendance.clock_out_photo_url}
                            alt="Punch Out"
                            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                            onClick={() => {
                              setSelectedImageUrl(attendance.clock_out_photo_url || null);
                              setSelectedImageAlt('Punch Out Photo');
                              setImageDialogOpen(true);
                            }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: '#999' }}>No photo</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationOn sx={{ fontSize: 16, color: '#666666' }} />
                          <Typography variant="body2" sx={{ color: '#666666' }}>
                            {attendance.location || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
              Attendance Analytics
            </Typography>
            <Button variant="outlined" startIcon={<Download />}>
              Export Report
            </Button>
          </Box>

          <Grid container spacing={3}>
            {/* Attendance Trend Chart */}
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600, mb: 2 }}>
                    Attendance Trend
                  </Typography>
                  <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#666666' }}>
                      Chart visualization will be implemented here
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Performance Metrics */}
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600, mb: 2 }}>
                    Performance Metrics
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Attendance Rate</Typography>
                      <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 600 }}>
                        {attendanceStats?.attendancePercentage || 0}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Avg Hours/Day</Typography>
                      <Typography variant="h6" sx={{ color: '#2196f3', fontWeight: 600 }}>
                        {attendanceStats?.averageHours || 0}h
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Total Hours</Typography>
                      <Typography variant="h6" sx={{ color: '#9c27b0', fontWeight: 600 }}>
                        {attendanceStats?.totalHours || 0}h
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Late Arrivals</Typography>
                      <Typography variant="h6" sx={{ color: '#ff9800', fontWeight: 600 }}>
                        {attendanceStats?.lateDays || 0}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Weekly Breakdown */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600, mb: 2 }}>
                    Weekly Breakdown
                  </Typography>
                  <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#666666' }}>
                      Weekly attendance chart will be implemented here
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Image Dialog */}
      <ImageDialog
        open={imageDialogOpen}
        onClose={() => {
          setImageDialogOpen(false);
          setSelectedImageUrl(null);
          setSelectedImageAlt('');
        }}
        imageUrl={selectedImageUrl}
        alt={selectedImageAlt}
      />
    </Box>
  );
};

export default UserAttendancePage;
